/**
 * Learn-a-subject fact banks. Each fact is one typeable sentence, so you absorb facts
 * while practising. Sets can be played solo or used as the sentence pack for a race.
 */
import type { Lesson } from './lessons.js';

export interface FactSet {
  id: string;
  title: string;
  facts: string[];
}
export interface Subject {
  id: string;
  name: string;
  icon: string;
  sets: FactSet[];
}

export const SUBJECTS: Subject[] = [
  {
    id: 'space',
    name: 'Space',
    icon: '🚀',
    sets: [
      { id: 'solar-system', title: 'The solar system', facts: [
        'Light from the Sun takes about eight minutes to reach Earth.',
        'Jupiter is the largest planet in our solar system.',
        'A day on Venus is longer than its year.',
        'The Moon drifts away from Earth by about four centimetres a year.',
        'Mercury is the closest planet to the Sun, but Venus is the hottest.',
        'Saturn is so light that it would float in a big enough bathtub.',
      ] },
      { id: 'sun-and-stars', title: 'The Sun and the stars', facts: [
        'The Sun holds more than ninety-nine percent of the mass of the solar system.',
        'About one million Earths could fit inside the Sun.',
        'Neutron stars can spin hundreds of times every second.',
        'A light year is the distance light travels in a year, nearly ten trillion kilometres.',
        'Our galaxy, the Milky Way, is about one hundred thousand light years across.',
        'Black holes have gravity so strong that not even light can escape.',
      ] },
      { id: 'exploration', title: 'Space exploration', facts: [
        'The first human in space was Yuri Gagarin in 1961.',
        'Neil Armstrong walked on the Moon in July 1969.',
        'Footprints on the Moon could last for millions of years because there is no wind.',
        'The International Space Station orbits Earth about every ninety minutes.',
        'Pluto was reclassified as a dwarf planet in 2006.',
        'Space is silent because there is no air to carry sound.',
      ] },
      { id: 'strange-worlds', title: 'Strange worlds', facts: [
        'Uranus rotates on its side, tilted at about ninety-eight degrees.',
        'Mars has the tallest volcano known, Olympus Mons, nearly three times the height of Everest.',
        'The surface of Venus is around four hundred and sixty-five degrees Celsius.',
        'Halley\'s Comet returns to the inner solar system about every seventy-six years.',
        'Jupiter\'s Great Red Spot is a storm larger than Earth.',
        'There are more stars in the universe than grains of sand on all of Earth\'s beaches.',
      ] },
    ],
  },
  {
    id: 'animals',
    name: 'Animals',
    icon: '🦁',
    sets: [
      { id: 'ocean', title: 'Ocean life', facts: [
        'An octopus has three hearts and blue blood.',
        'The blue whale is the largest animal that has ever lived.',
        'The tongue of a blue whale can weigh as much as an elephant.',
        'Sea otters hold hands while they sleep so they do not drift apart.',
        'Dolphins sleep with one half of their brain at a time.',
        'Sharks have existed for longer than trees.',
      ] },
      { id: 'land', title: 'Land animals', facts: [
        'Elephants are the largest land animals alive today.',
        'Cheetahs are the fastest land animals, reaching over one hundred kilometres per hour.',
        'Sloths can take a month to digest a single meal.',
        'Giraffes have the same number of neck bones as humans: seven.',
        'Koalas sleep for up to twenty hours a day.',
        'Kangaroos cannot easily walk backwards.',
      ] },
      { id: 'birds', title: 'Birds', facts: [
        'A group of flamingos is called a flamboyance.',
        'Owls cannot move their eyes, so they turn their heads instead.',
        'A hummingbird\'s heart can beat more than one thousand times a minute.',
        'Crows can recognise individual human faces.',
        'The Arctic tern migrates from the Arctic to the Antarctic and back each year.',
        'Some penguins offer their mate a pebble as a gift.',
      ] },
      { id: 'small-creatures', title: 'Small creatures', facts: [
        'Honeybees tell each other where flowers are with a waggle dance.',
        'Butterflies taste with their feet.',
        'Ants can carry many times their own body weight.',
        'A snail can sleep for up to three years.',
        'Bats are the only mammals capable of true flight.',
        'Cows form close friendships and get stressed when separated.',
      ] },
    ],
  },
  {
    id: 'body',
    name: 'Human body',
    icon: '🫀',
    sets: [
      { id: 'bones', title: 'Bones and muscles', facts: [
        'An adult human body has two hundred and six bones.',
        'Babies are born with around three hundred bones that later fuse together.',
        'The smallest bone in the body is in the ear and is called the stapes.',
        'The femur is the longest and strongest bone in the body.',
        'The strongest muscle by weight is the masseter, the jaw muscle.',
        'Laughing works the muscles in your face, chest and abdomen.',
      ] },
      { id: 'heart-blood', title: 'Heart and blood', facts: [
        'Your heart beats about one hundred thousand times a day.',
        'Blood makes up about seven to eight percent of your body weight.',
        'Red blood cells live for about one hundred and twenty days.',
        'Your blood vessels would stretch for around one hundred thousand kilometres.',
        'Your lungs contain hundreds of millions of tiny air sacs called alveoli.',
        'The liver can regrow large parts of itself.',
      ] },
      { id: 'senses', title: 'Brain and senses', facts: [
        'The brain uses about twenty percent of the body\'s energy.',
        'Nerve signals can travel at over one hundred metres per second.',
        'Your eyes blink around fifteen to twenty times a minute.',
        'The human nose can tell apart around a trillion different smells.',
        'Humans are the only animals known to blush.',
        'Stomach acid is strong enough to dissolve some metals.',
      ] },
      { id: 'skin-growth', title: 'Skin and growth', facts: [
        'Skin is the largest organ of the human body.',
        'Humans shed millions of skin cells every day.',
        'Fingernails grow faster than toenails.',
        'Your stomach lining renews itself every few days.',
        'Adults have thirty-two teeth, including the wisdom teeth.',
        'In a lifetime the average person walks far enough to circle the Earth several times.',
      ] },
    ],
  },
  {
    id: 'geography',
    name: 'Geography',
    icon: '🌍',
    sets: [
      { id: 'records', title: 'World records', facts: [
        'Russia is the largest country in the world by area.',
        'Vatican City is the smallest country in the world.',
        'Mount Everest is the highest mountain above sea level.',
        'The Pacific is the largest and deepest ocean.',
        'The Sahara is the largest hot desert in the world.',
        'Angel Falls in Venezuela is the tallest waterfall in the world.',
      ] },
      { id: 'water', title: 'Rivers, lakes and seas', facts: [
        'The Nile and the Amazon are the two longest rivers on Earth.',
        'Canada has more lakes than any other country.',
        'Lake Baikal in Russia is the deepest lake in the world.',
        'The Dead Sea is one of the saltiest bodies of water on Earth.',
        'The Mariana Trench is the deepest point in the oceans.',
        'The Great Barrier Reef is the largest living structure on Earth.',
      ] },
      { id: 'continents', title: 'Continents and countries', facts: [
        'Australia is the only country that is also a continent.',
        'Antarctica is the coldest, driest and windiest continent.',
        'Africa is the only continent that spans all four hemispheres.',
        'Istanbul is the only major city that sits on two continents.',
        'Brazil borders every South American country except Chile and Ecuador.',
        'Greenland is the largest island that is not a continent.',
      ] },
      { id: 'curiosities', title: 'Curiosities', facts: [
        'Iceland has no mosquitoes.',
        'Nepal\'s flag is the only national flag that is not a rectangle.',
        'Japan is made up of thousands of islands.',
        'The Andes is the longest mountain range on land.',
        'Alaska is the largest state in the United States.',
        'The Amazon is the largest rainforest on Earth.',
      ] },
    ],
  },
  {
    id: 'history',
    name: 'History',
    icon: '🏛️',
    sets: [
      { id: 'ancient', title: 'Ancient world', facts: [
        'The Great Pyramid of Giza was built around 2560 BC.',
        'The ancient Olympic Games were held in Olympia, Greece.',
        'Cleopatra lived closer in time to the Moon landing than to the building of the Great Pyramid.',
        'The Roman Empire lasted, in one form or another, for well over a thousand years.',
        'The Great Wall of China is thousands of kilometres long.',
        'Oxford University is older than the Aztec Empire.',
      ] },
      { id: 'middle-ages', title: 'Middle Ages to Renaissance', facts: [
        'The Magna Carta was signed in 1215.',
        'The Black Death killed a large part of Europe\'s population in the fourteenth century.',
        'The Renaissance began in Italy in the fourteenth century.',
        'The printing press was invented by Johannes Gutenberg around 1440.',
        'Christopher Columbus reached the Americas in 1492.',
        'Shakespeare wrote around thirty-seven plays.',
      ] },
      { id: 'modern', title: 'Revolutions and firsts', facts: [
        'The French Revolution began in 1789.',
        'The Eiffel Tower was completed in 1889 for the World\'s Fair.',
        'The first modern Olympic Games were held in Athens in 1896.',
        'The Wright brothers made the first powered flight in 1903.',
        'The Titanic sank in April 1912 on its maiden voyage.',
        'Some women in the United Kingdom gained the vote in 1918, and all women in 1928.',
      ] },
      { id: 'twentieth', title: 'The twentieth century', facts: [
        'World War One began in 1914 and ended in 1918.',
        'The first football World Cup was held in Uruguay in 1930.',
        'World War Two ended in 1945.',
        'The first email was sent in 1971.',
        'The Berlin Wall fell in 1989.',
        'Nelson Mandela became president of South Africa in 1994.',
      ] },
    ],
  },
  {
    id: 'science',
    name: 'Science and inventions',
    icon: '🔬',
    sets: [
      { id: 'matter', title: 'Matter and energy', facts: [
        'Water is made of two hydrogen atoms and one oxygen atom.',
        'Water expands when it freezes, which is why ice floats.',
        'Diamonds and pencil graphite are both made of carbon.',
        'Helium is lighter than air, which is why balloons float.',
        'The periodic table has one hundred and eighteen known elements.',
        'Gold is so soft it can be hammered into sheets thin enough to see through.',
      ] },
      { id: 'light-sound', title: 'Light, sound and weather', facts: [
        'Light travels at nearly three hundred thousand kilometres per second.',
        'Sound travels about four times faster in water than in air.',
        'Lightning is about five times hotter than the surface of the Sun.',
        'Hot water can sometimes freeze faster than cold water, known as the Mpemba effect.',
        'Glass is made by melting sand at very high temperatures.',
        'Bananas are slightly radioactive because they contain potassium.',
      ] },
      { id: 'inventors', title: 'Inventors and discoverers', facts: [
        'The telephone was patented by Alexander Graham Bell in 1876.',
        'Penicillin was discovered by Alexander Fleming in 1928.',
        'Ada Lovelace wrote the first computer program in the 1840s.',
        'Marie Curie won Nobel Prizes in both physics and chemistry.',
        'The World Wide Web was invented by Tim Berners-Lee in 1989.',
        'Aluminium was once more valuable than gold, before cheap refining was invented.',
      ] },
      { id: 'everyday', title: 'Everyday science', facts: [
        'Honey never spoils; edible honey has been found in ancient tombs.',
        'Velcro was inspired by burrs sticking to a dog\'s fur.',
        'The microwave oven was invented after an engineer noticed a melted chocolate bar near a radar.',
        'Rubber bands last longer when kept in a refrigerator.',
        'Hot air rises because it is less dense than cold air.',
        'Sharkskin inspired swimsuits and boat coatings that reduce drag.',
      ] },
    ],
  },
];

export function subjectById(id: string): Subject | undefined {
  return SUBJECTS.find((s) => s.id === id);
}

/** All facts for a subject, for use as a race sentence pack. */
export function subjectSentences(subject: Subject): string[] {
  return subject.sets.flatMap((s) => s.facts);
}

export function subjectLessons(subject: Subject): Lesson[] {
  return subject.sets.map((set) => ({
    id: `subj-${subject.id}-${set.id}`,
    title: `${subject.icon} ${subject.name}: ${set.title}`,
    description: `${set.facts.length} facts to type and remember.`,
    keys: '',
    lines: set.facts,
    targetWpm: 30,
  }));
}

/** Sentence packs selectable for races: motivation (default) plus every subject. */
export const PACKS: { id: string; name: string }[] = [
  { id: 'motivation', name: '💪 Motivation' },
  ...SUBJECTS.map((s) => ({ id: s.id, name: `${s.icon} ${s.name}` })),
];
